import React, { ChangeEvent } from "react";
import ProductItem from "./components/ProductItem";
import SummaryRow from "./components/SummaryRow";
import { Productbtn } from "../../../../../shared/services/interfacev2";
import { useFormatAmount } from "../hooks/useFormatAmount";
import CommonButtons from "../../../../../shared/components/CommonButtons";
import { Truck } from "lucide-react";

interface OrderSummaryProps {
  selectedProducts: Productbtn[];
  valueMap: { [id: string]: number };
  totalQuantity: number;
  totalAmount: number;
  condition: number;
  disValue: number;
  deliveryFee: number;
  deliveryFeeEnabled: boolean;
  onQuantityChange: (productId: string, amount: string, type: "add" | "subtract") => void;
  onProductRemove: (id: string, amount: string) => void;
  onDiscountChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onDeliveryFeeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCheckout: () => void;
  onNewOrder: () => void;
  grandTotal: number;
  // New props for amount editing
  onAmountEdit?: (productId: string, newAmount: string) => void;
  isAmountEditable?: boolean;
  getProductDisplayAmount?: (product: Productbtn) => string; // New prop to get display amount
  editedAmounts?: { [productId: string]: string }; // New prop to pass edited amounts
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  selectedProducts,
  valueMap,
  totalQuantity,
  totalAmount,
  condition,
  disValue,
  deliveryFee,
  deliveryFeeEnabled,
  onQuantityChange,
  onProductRemove,
  onDiscountChange,
  onDeliveryFeeChange,
  onCheckout,
  onNewOrder,
  grandTotal,
  onAmountEdit,
  isAmountEditable = true,
  editedAmounts = {},
}) => {
  const formatAmount = useFormatAmount();

  // Validate amount before saving
  const handleAmountEdit = (productId: string, newAmount: string) => {
    if (!onAmountEdit) return;

    const numericAmount = parseFloat(newAmount);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      // Show error or notification
      console.error("Invalid amount entered");
      return;
    }

    // Check business rules/permissions
    if (!isAmountEditable) {
      console.error("User does not have permission to edit amounts");
      return;
    }

    // Call the parent handler with validated amount
    onAmountEdit(productId, numericAmount.toFixed(2));
  };

  // Get the display amount for a product
  const getProductDisplayAmount = (product: Productbtn) => {
    return editedAmounts[product._id.$oid] || product.amount;
  };

  return (
    <div className="xl:w-4/12 lg:w-5/12 w-full p-2 flex flex-col flex-shrink-0 md:max-h-[95vh] max-h-[calc(100vh-80px)]" id="order-summary">
      {/* Header */}
      <div className="bg-[#F8F9FA] rounded-full px-4 py-2 text-2xl mb-2 flex items-center justify-between">
        <span>Order summary</span>

        {/* Total quantity badge */}
        <span className="rounded-full px-4 py-1 text-base font-semibold">{totalQuantity} Nos</span>
      </div>

      {/* Product List */}
      <div className="thin-scrollbar bg-[#F8F9FA] rounded-xl p-1.5 flex-1 overflow-y-auto -mt-1 mb-1 min-h-[250px]">
        {selectedProducts.length > 0 ? (
          selectedProducts.map((product: Productbtn) => {
            const displayAmount = getProductDisplayAmount(product);
            const quantity = valueMap[`${product._id.$oid}-${product.amount}`] || 1;

            return (
              <ProductItem
                key={product._id.$oid}
                product={product}
                displayAmount={displayAmount} // Pass display amount
                quantity={quantity}
                onQuantityChange={onQuantityChange}
                onProductRemove={onProductRemove}
                onAmountEdit={handleAmountEdit}
                isAmountEditable={isAmountEditable}
              />
            );
          })
        ) : (
          <div className="h-[300px] flex items-center justify-center text-gray-400">No products selected</div>
        )}
      </div>

      {/* Totals */}
      <div className="bg-white rounded-xl p-2 space-y-1 text-[18px] sm:text-[16px] md:text-[18px]">
        <SummaryRow label="Subtotal" value={`${formatAmount(totalAmount)} QAR`} />

        {/* Delivery Fee */}
        {deliveryFeeEnabled && (
          <div className="flex items-center">
            <p className="w-[180px] flex items-center gap-1">
              Delivery fee (+)
              <Truck size={18} className="text-gray-600" />
            </p>
            <p className="w-4 text-center">:</p>

            <div className="flex-1 flex justify-end items-center gap-2">
              <input
                type="number"
                value={deliveryFee === 0 ? "" : deliveryFee}
                onChange={onDeliveryFeeChange}
                placeholder="0"
                className="w-24 sm:w-20 border border-black px-2 py-1 rounded-xl text-right transition"
              />
            </div>
          </div>
        )}

        {/* Discount */}
        <div className="flex items-center">
          <p className="w-[180px]">Discount (-)</p>
          <p className="w-4 text-center">:</p>
          <div className="flex-1 flex justify-end items-center gap-2">
            <input type="number" value={condition === 0 ? "" : condition} onChange={onDiscountChange} placeholder="0" className="w-24 sm:w-20 border border-black px-2 py-1 rounded-xl text-right" />
          </div>
        </div>

        {/* Grand Total */}
        <SummaryRow label="Grand Total" value={`${formatAmount(grandTotal)} QAR`} />

        {/* Actions */}
        <div className="flex flex-col sm:flex-row justify-between mt-4 text-xl gap-2">
          <CommonButtons label="NEW ORDER" className="w-full sm:w-[180px] h-[50px] font-bold text-xl rounded-full" variant="neworder" onClick={onNewOrder} />
          <CommonButtons label="CHECKOUT" className="w-full sm:w-[180px] h-[50px] font-bold text-xl rounded-full" onClick={onCheckout} />
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
